import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import contractService, { Contract } from '../../services/contractService';
import { parseRouteId } from '../../utils/routeId';
import { showAlert } from '../../utils/alert';
import { RiArrowLeftLine, RiTimeLine, RiUserLine, RiMailLine, RiFileTextLine, RiCheckDoubleLine } from 'react-icons/ri';
import Layout from '../../components/ui/Layout';
import { usePermission } from '../../hooks/usePermission';

interface DocumentLog {
  id: number;
  action: string;
  notes?: string;
  sent_to_email?: string;
  signed_by_name?: string;
  action_by_user_id: number;
  created_at: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
}

/** ป้ายสถานะเอกสาร — ชุดเดียวกับหน้าติดตามเอกสาร (ContractDocumentTrackingPage.getStatusText) */
const DOCUMENT_STATUS_LABEL: Record<string, string> = {
  PENDING: 'รอดำเนินการ',
  DRAFT: 'ฉบับร่าง',
  GENERATED: 'สร้าง PDF แล้ว',
  SENT: 'ส่งให้ลูกค้าแล้ว',
  CUSTOMER_SIGNED: 'ลูกค้าเซ็นแล้ว',
  COMPLETED: 'เสร็จสมบูรณ์',
};

const ContractDocumentLogsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canView } = usePermission();
  const [contract, setContract] = useState<Contract | null>(null);
  const [logs, setLogs] = useState<DocumentLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [logsError, setLogsError] = useState<boolean>(false);

  // ตรวจสอบ permission
  useEffect(() => {
    if (!canView('contracts')) {
      showAlert('ไม่มีสิทธิ์เข้าถึง', 'คุณไม่มีสิทธิ์ในการดูประวัติเอกสารสัญญา', 'error');
      navigate('/contracts');
    }
  }, [canView, navigate]);

  // BUG-S2283-001: id ต้องเป็นจำนวนเต็มบวกล้วน — ไม่ถูกต้อง = ไม่ยิง API (เดิม "abc" ยิง /contracts/NaN)
  const contractId = parseRouteId(id);

  useEffect(() => {
    if (contractId === null) {
      setContract(null);
      setLoading(false);
      return;
    }
    loadData(contractId);
  }, [contractId]);

  const loadData = async (contractId: number) => {
    try {
      setLoading(true);
      let contractLoaded = false;
      
      // โหลดข้อมูลสัญญา
      try {
        const contractData = await contractService.getContract(contractId);
        setContract(contractData);
        contractLoaded = true;
      } catch (contractError) {
        console.error('Could not load contract:', contractError);
        showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลสัญญาได้', 'error');
        return;
      }
      
      // โหลดประวัติเอกสาร
      try {
        const logsData = await contractService.getDocumentLogs(contractId);
        setLogs(logsData || []);
        setLogsError(false);
      } catch (logError) {
        console.error('Could not load document logs:', logError);
        setLogs([]);
        setLogsError(true);
        // ไม่แสดง error alert สำหรับ logs เพราะยังแสดงหน้าได้
        console.warn('Proceeding without document logs due to error');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showAlert('เกิดข้อผิดพลาดทั่วไป', 'ไม่สามารถโหลดข้อมูลได้', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action.toUpperCase()) {
      case 'GENERATED':
        return <RiFileTextLine className="w-5 h-5 text-info-600" />;
      case 'SENT':
        return <RiMailLine className="w-5 h-5 text-warning-600" />;
      case 'CUSTOMER_SIGNED':
      case 'SIGNED':
        return <RiCheckDoubleLine className="w-5 h-5 text-success-600" />;
      case 'COMPLETED':
        return <RiFileTextLine className="w-5 h-5 text-accent-600" />;
      default:
        return <RiTimeLine className="w-5 h-5 text-secondary-600" />;
    }
  };

  const getActionText = (action: string) => {
    switch (action.toUpperCase()) {
      // BUG-S2074-006: backend เขียน action = GENERATED/SENT/CUSTOMER_SIGNED/COMPLETED
      // เดิมหน้านี้รู้จักแค่ 'SIGNED' (ไม่มีใครเขียน) → 2 ใน 4 ขั้นขึ้นเป็นภาษาอังกฤษดิบ
      case 'GENERATED':
        return 'สร้างเอกสาร PDF';
      case 'SENT':
        return 'ส่งเอกสารให้ลูกค้า';
      case 'CUSTOMER_SIGNED':
      case 'SIGNED':
        return 'ลูกค้าเซ็นเอกสาร';
      case 'COMPLETED':
        return 'เอกสารเสร็จสมบูรณ์';
      default:
        return action;
    }
  };

  const getActionColor = (action: string) => {
    switch (action.toUpperCase()) {
      case 'GENERATED':
        return 'bg-info-50 border-info-200';
      case 'SENT':
        return 'bg-warning-50 border-warning-200';
      case 'CUSTOMER_SIGNED':
      case 'SIGNED':
        return 'bg-success-50 border-success-200';
      case 'COMPLETED':
        return 'bg-accent-50 border-accent-200';
      default:
        return 'bg-secondary-50 border-secondary-200';
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('th-TH', {
      year: '2-digit',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-info-600"></div>
        </div>
      </Layout>
    );
  }

  // BUG-S2283-001: id ใน URL ไม่ใช่เลขที่ถูกต้อง → แจ้งไม่พบแทนหน้าเปล่า
  if (contractId === null) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[40vh]">
          <p className="text-secondary-600">ไม่พบข้อมูลสัญญา</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header - Executive Navy Theme */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <RiTimeLine className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-secondary-900 leading-tight">ประวัติเอกสารสัญญา</h1>
            <p className="text-xs text-secondary-500 mt-0.5">ดูประวัติการเปลี่ยนแปลงเอกสารสัญญา</p>
          </div>
        </div>
      </div>

      {/* Contract Summary */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <span className="text-sm text-secondary-500">เลขที่สัญญา</span>
            <p className="font-medium">{contract?.contract_number}</p>
          </div>
          <div>
            <span className="text-sm text-secondary-500">บริษัท</span>
            <p className="font-medium">{contract?.company?.name}</p>
          </div>
          <div>
            <span className="text-sm text-secondary-500">สถานะเอกสาร</span>
            <p className="font-medium">
              <span className="px-2 py-1 bg-info-100 text-info-800 text-sm rounded-full">
                {DOCUMENT_STATUS_LABEL[contract?.document_status || 'PENDING'] || contract?.document_status}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Document Logs Timeline */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-secondary-200">
          <h2 className="text-lg font-semibold text-secondary-800 flex items-center">
            <RiTimeLine className="w-5 h-5 mr-2" />
            ประวัติการดำเนินการ
          </h2>
        </div>

        <div className="p-6">
          {logsError ? (
            <div className="text-center py-8">
              <RiFileTextLine className="w-12 h-12 text-danger-400 mx-auto mb-4" />
              <p className="text-danger-500">ไม่สามารถโหลดประวัติการดำเนินการได้</p>
              <p className="text-sm text-secondary-500 mt-2">กรุณาลองใหม่อีกครั้ง</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8">
              <RiFileTextLine className="w-12 h-12 text-secondary-400 mx-auto mb-4" />
              <p className="text-secondary-500">ยังไม่มีประวัติการดำเนินการ</p>
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log, index) => (
                <div key={log.id} className="relative">
                  {/* Timeline line */}
                  {index < logs.length - 1 && (
                    <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-secondary-200"></div>
                  )}
                  
                  <div className={`flex items-start space-x-4 p-4 rounded-lg border ${getActionColor(log.action)}`}>
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white border-2 border-current flex items-center justify-center">
                      {getActionIcon(log.action)}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-secondary-900">
                          {getActionText(log.action)}
                        </h3>
                        <span className="text-sm text-secondary-500">
                          {formatDateTime(log.created_at)}
                        </span>
                      </div>
                      
                      <div className="mt-2 space-y-1">
                        {log.user && (
                          <p className="text-sm text-secondary-600 flex items-center">
                            <RiUserLine className="w-4 h-4 mr-1" />
                            ดำเนินการโดย: {log.user.name}
                          </p>
                        )}
                        
                        {log.sent_to_email && (
                          <p className="text-sm text-secondary-600 flex items-center">
                            <RiMailLine className="w-4 h-4 mr-1" />
                            ส่งไปที่: {log.sent_to_email}
                          </p>
                        )}
                        
                        {log.signed_by_name && (
                          <p className="text-sm text-secondary-600 flex items-center">
                            <RiCheckDoubleLine className="w-4 h-4 mr-1" />
                            เซ็นโดย: {log.signed_by_name}
                          </p>
                        )}
                        
                        {log.notes && (
                          <p className="text-sm text-secondary-600">
                            หมายเหตุ: {log.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ContractDocumentLogsPage;